/**
 * Gets a user's full name from user object
 * @param user - User object with firstName and lastName
 * @returns Full name string
 */
export function getUsersName(
  user: {
    firstName?: string | null
    lastName?: string | null
  } | null
): string {
  if (!user) return ''
  return `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
}

/**
 * Validates a URL string
 * @param string - URL string to validate
 * @returns null if valid, error message if invalid
 */
export function validateUrl(string: string): string | null {
  const res = string.match(
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g
  )
  if (res !== null) return null
  else return 'Enter a valid url'
}

/**
 * Validates an email address
 * @param email - Email string to validate
 * @returns true if valid, false otherwise
 */
export function validateEmail(email: string): boolean {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

/**
 * Validates a phone number
 * @param phone - Phone string to validate
 * @returns true if valid, false otherwise
 */
export function validatePhone(phone: string): boolean {
  const regex = /^\+?[1-9][0-9]{7,14}$/
  return regex.test(phone)
}

/**
 * Validates a password
 * Must have at least 6 characters, one number, and one capital letter
 * @param password - Password string to validate
 * @returns true if valid, false otherwise
 */
export function validatePassword(password: string | null | undefined): boolean {
  if (!password) return false
  return password.length >= 6 && /[A-Z]/.test(password) && /[0-9]/.test(password)
}

/**
 * Validates that password and confirm password match
 * @param password - Password string
 * @param confirmPassword - Confirm password string
 * @returns true if valid, false otherwise
 */
export function validateConfirmPassword(
  password: string | null | undefined,
  confirmPassword: string | null | undefined
): boolean {
  return (
    (password?.length || 0) >= 6 &&
    (confirmPassword?.length || 0) >= 6 &&
    password === confirmPassword
  )
}

/**
 * Checks if an email already exists in a members array
 * @param email - Email to check
 * @param members - Array of members
 * @returns true if email exists, false otherwise
 */
export function validateEmailDoesntExist(
  email: string,
  members: Array<{ email?: string }>
): boolean {
  return !!members.find((x) => x.email === email)
}

/**
 * Generates a random password string
 * @param count - Length of password to generate
 * @returns Random password string
 */
export function generateRandomPassword(count: number): string {
  const letter =
    '0123456789ABCDEFGHIJabcdefghijklmnopqrstuvwxyzKLMNOPQRSTUVWXYZ0123456789abcdefghiABCDEFGHIJKLMNOPQRST0123456789jklmnopqrstuvwxyz'
  let randomString = ''
  for (let i = 0; i < count; i++) {
    const randomStringNumber = Math.floor(1 + Math.random() * (letter.length - 1))
    randomString += letter.substring(randomStringNumber, randomStringNumber + 1)
  }
  return randomString
}
